import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { captureReferralFromUrl } from './utils/referral';
import { HomePage } from './pages/HomePage';
import { DashboardV2Page } from './pages/DashboardV2Page';
import { DealsPage } from './pages/DealsPage';
import { FlipsPage } from './pages/FlipsPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { CollectionDetailPage } from './pages/CollectionDetailPage';
import { NFTDetailPage } from './pages/NFTDetailPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { ReferralsPage } from './pages/ReferralsPage';
import { AmbassadorsPage } from './pages/AmbassadorsPage';
import { AdminAmbassadorsPage } from './pages/AdminAmbassadorsPage';
import { AdminAmbassadorDetailPage } from './pages/AdminAmbassadorDetailPage';
import { AdminAmbassadorApplicationDetailPage } from './pages/AdminAmbassadorApplicationDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminCollectionsPage } from './pages/AdminCollectionsPage';
import { AdminCollectionDetailPage } from './pages/AdminCollectionDetailPage';
import { AdminAddCollectionPage } from './pages/AdminAddCollectionPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminUserDetailPage } from './pages/AdminUserDetailPage';
import { AdminProjectsPage } from './pages/AdminProjectsPage';
import { AdminProjectDetailPage } from './pages/AdminProjectDetailPage';
import { AdminMarketplacesPage } from './pages/AdminMarketplacesPage';
import { AdminLaunchesPage } from './pages/AdminLaunchesPage';
import { AdminLaunchReviewPage } from './pages/AdminLaunchReviewPage';
import { AdminSettlementsPage } from './pages/AdminSettlementsPage';
import { AdminReferralPayoutsPage } from './pages/AdminReferralPayoutsPage';
import { AdminLaunchpadAnalyticsPage } from './pages/AdminLaunchpadAnalyticsPage';
import { AdminLaunchpadHealthPage } from './pages/AdminLaunchpadHealthPage';
import { AdminTradesPage } from './pages/AdminTradesPage';
import { AdminTradeDetailPage } from './pages/AdminTradeDetailPage';
import { AdminBundlesPage } from './pages/AdminBundlesPage';
import { AdminBundleDetailPage } from './pages/AdminBundleDetailPage';
import { AdminOffersPage } from './pages/AdminOffersPage';
import { AdminTransactionsPage } from './pages/AdminTransactionsPage';
import { AdminLedgerHealthPage } from './pages/AdminLedgerHealthPage';
import { AdminLedgerRecoveryPage } from './pages/AdminLedgerRecoveryPage';
import { AdminNotificationsPage } from './pages/AdminNotificationsPage';
import { AdminMarketplaceActivityPage } from './pages/AdminMarketplaceActivityPage';
import { AdminFailedTransactionsPage } from './pages/AdminFailedTransactionsPage';
import { AdminRoute } from './components/AdminRoute';
import { WalletPage } from './pages/WalletPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { InboxPage } from './pages/InboxPage';
import TradesPage from './pages/TradesPage';
import { DevEmailPreviewPage } from './pages/DevEmailPreviewPage';
import { DevCrashPage } from './pages/DevCrashPage';
import { LaunchpadListPage } from './pages/LaunchpadListPage';
import { LaunchpadCreatePage } from './pages/LaunchpadCreatePage';
import { MintPage } from './pages/MintPage';
import { LaunchpadDashboardPage } from './pages/LaunchpadDashboardPage';
import { LaunchpadGuidePage } from './pages/LaunchpadGuidePage';
import { DropsListingPage } from './pages/DropsListingPage';
import { ClaimPage } from './pages/ClaimPage';
import { DropRedirect } from './pages/DropRedirect';
import { AllowlistRegistrationPage } from './pages/AllowlistRegistrationPage';
import { PartnerPage } from './pages/PartnerPage';
import { CreatorDashboardPage } from './pages/CreatorDashboardPage';
import { MigrateListingsPage } from './pages/MigrateListingsPage';
import { ComposePage } from './pages/ComposePage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { featureFlags } from './config/featureFlags';
import { CollectionManagePage } from './pages/CollectionManagePage';
import { ProjectManagePage } from './pages/ProjectManagePage';
import { SweepstakesPage } from './pages/SweepstakesPage';
import { IntelligencePage } from './pages/IntelligencePage';
import { NotificationContainer } from './components/notifications/NotificationContainer';
import { ConnectionStatus } from './components/ConnectionStatus';
import { EmailCollectionModal, useEmailCollectionModal } from './components/EmailCollectionModal';
import { useAuthStore } from './store/authStore';
import { useSSEStore } from './store/sseStore';
import { getProfile } from './api/auth';
import { DefaultPageLayout } from './ui';

function App() {
  const { isAuthenticated, updateUser } = useAuthStore();
  const { isOpen, handleClose, handleSkip } = useEmailCollectionModal();

  // Capture referral code from URL on app load
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  // Refresh user profile from server on app load to pick up server-side changes
  useEffect(() => {
    if (!isAuthenticated) return;
    getProfile().then((freshUser) => {
      // Preserve the active wallet address — the profile endpoint returns the
      // primary wallet address from the users table, which would overwrite the
      // switched-to wallet address and break multi-wallet switching.
      const { xrpl_address: _primary, ...profileUpdates } = freshUser;
      updateUser(profileUpdates);
    }).catch(() => {
      // Silently ignore — stale cache is acceptable as fallback
    });
  }, [isAuthenticated]);

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes - no layout wrapper, pages handle their own layout */}
        {/* The v2 dashboard is the default landing page. The original
            HomePage is kept around at /dashboard-v1 as a fallback/reference —
            not linked from the nav, but accessible if we need to compare. */}
        <Route index element={<DashboardV2Page />} />
        <Route path="dashboard2" element={<DashboardV2Page />} />
        <Route path="dashboard-v1" element={<HomePage />} />
        <Route path="intelligence" element={<IntelligencePage />} />
        <Route path="deals" element={<DealsPage />} />
        <Route path="flip" element={<FlipsPage />} />
        <Route path="collections" element={<CollectionsPage />} />
        <Route path="collections/:id" element={<CollectionDetailPage />} />
        <Route path="collections/:slug/contest" element={<SweepstakesPage />} />
        <Route path="collections/:slug/contest/:contestId" element={<SweepstakesPage />} />
        {featureFlags.composable && <Route path="collections/:slug/compose" element={<ComposePage />} />}
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:slug" element={<ProjectDetailPage />} />
        {/* IMPORTANT: NFT detail route is /nfts/ (plural), NOT /nft/ */}
        <Route path="nfts/:tokenId" element={<NFTDetailPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="referrals" element={<ReferralsPage />} />
        <Route path="ambassadors" element={<AmbassadorsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="trades" element={<TradesPage />} />
        <Route path="admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        <Route path="admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
        <Route path="admin/users/:userId" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
        <Route path="admin/collections" element={<AdminRoute><AdminCollectionsPage /></AdminRoute>} />
        <Route path="admin/collections/add" element={<AdminRoute><AdminAddCollectionPage /></AdminRoute>} />
        <Route path="admin/collections/:issuer/:taxon" element={<AdminRoute><AdminCollectionDetailPage /></AdminRoute>} />
        <Route path="admin/projects" element={<AdminRoute><AdminProjectsPage /></AdminRoute>} />
        <Route path="admin/projects/:id" element={<AdminRoute><AdminProjectDetailPage /></AdminRoute>} />
        <Route path="admin/marketplaces" element={<AdminRoute><AdminMarketplacesPage /></AdminRoute>} />
        <Route path="admin/launches" element={<AdminRoute><AdminLaunchesPage /></AdminRoute>} />
        <Route path="admin/launches/:id" element={<AdminRoute><AdminLaunchReviewPage /></AdminRoute>} />
        <Route path="admin/trades" element={<AdminRoute><AdminTradesPage /></AdminRoute>} />
        <Route path="admin/trades/:tradeId" element={<AdminRoute><AdminTradeDetailPage /></AdminRoute>} />
        <Route path="admin/settlements" element={<AdminRoute><AdminSettlementsPage /></AdminRoute>} />
        <Route path="admin/referral-payouts" element={<AdminRoute><AdminReferralPayoutsPage /></AdminRoute>} />
        <Route path="admin/ambassadors" element={<AdminRoute><AdminAmbassadorsPage /></AdminRoute>} />
        <Route path="admin/ambassadors/members/:id" element={<AdminRoute><AdminAmbassadorDetailPage /></AdminRoute>} />
        <Route path="admin/ambassadors/applications/:id" element={<AdminRoute><AdminAmbassadorApplicationDetailPage /></AdminRoute>} />
        <Route path="admin/launchpad-analytics" element={<AdminRoute><AdminLaunchpadAnalyticsPage /></AdminRoute>} />
        <Route path="admin/launchpad-health" element={<AdminRoute><AdminLaunchpadHealthPage /></AdminRoute>} />
        <Route path="admin/bundles" element={<AdminRoute><AdminBundlesPage /></AdminRoute>} />
        <Route path="admin/bundles/:bundleId" element={<AdminRoute><AdminBundleDetailPage /></AdminRoute>} />
        <Route path="admin/offers" element={<AdminRoute><AdminOffersPage /></AdminRoute>} />
        <Route path="admin/transactions" element={<AdminRoute><AdminTransactionsPage /></AdminRoute>} />
        <Route path="admin/ledger-health" element={<AdminRoute><AdminLedgerHealthPage /></AdminRoute>} />
        <Route path="admin/ledger-recovery" element={<AdminRoute><AdminLedgerRecoveryPage /></AdminRoute>} />
        <Route path="admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
        <Route path="admin/marketplace-activity" element={<AdminRoute><AdminMarketplaceActivityPage /></AdminRoute>} />
        <Route path="admin/failed-transactions" element={<AdminRoute><AdminFailedTransactionsPage /></AdminRoute>} />
        <Route path="wallets/:id" element={<WalletPage />} />
        <Route path="wallets/:id/:collectionId" element={<WalletPage />} />

        {/* Launchpad routes - accessible by direct URL only (not in nav bar) */}
        <Route path="launchpad/guide" element={<LaunchpadGuidePage />} />
        <Route path="launchpad" element={<LaunchpadListPage />} />
        <Route path="launchpad/new" element={<LaunchpadCreatePage />} />
        <Route path="mint" element={<MintPage />} />
        <Route path="launchpad/:id" element={<LaunchpadDashboardPage />} />

        {/* Public drops/mint pages */}
        <Route path="drops" element={<DropsListingPage />} />
        <Route path="drops/:slug/register" element={<AllowlistRegistrationPage />} />
        <Route path="drops/:slug" element={<DropRedirect />} />

        {/* Physical NFC card redemption — one URL per edition copy */}
        <Route path="claim/:token" element={<ClaimPage />} />

        {/* Xaman OAuth-style return URL — Xaman redirects here after sign-in */}
        <Route path="auth/callback" element={<AuthCallbackPage />} />

        {/* Marketplace migration - copy listings to xrp.deals */}
        <Route path="migrate" element={<MigrateListingsPage />} />

        {/* Partner program page */}
        <Route path="partner" element={<PartnerPage />} />

        {/* Creator Dashboard, Project Management, and Collection Management */}
        <Route path="creator" element={<CreatorDashboardPage />} />
        <Route path="creator/projects/:slug" element={<ProjectManagePage />} />
        <Route path="creator/projects/:slug/collections/:collectionSlug" element={<CollectionManagePage />} />
        <Route path="creator/collections/:collectionSlug" element={<CollectionManagePage />} />

        {/* Development-only routes */}
        {import.meta.env.DEV && (
          <>
            <Route path="dev/emails" element={<DevEmailPreviewPage />} />
            <Route path="dev/crash" element={<DevCrashPage />} />
          </>
        )}
      </Routes>

      {/* Toast notification container */}
      <NotificationContainer position="top-right" maxToasts={5} />

      {/* Connection status indicator */}
      <ConnectionStatus />

      {/* Email collection modal for first-time users */}
      {isAuthenticated && (
        <EmailCollectionModal
          isOpen={isOpen}
          onClose={handleClose}
          onSkip={handleSkip}
        />
      )}
    </ErrorBoundary>
  );
}

export default App;
